﻿<template>
    <b-container class="mt-4 p-sm-0 px-3 product-card-page">
        <div class="back-button mb-5">
            <b-button class="text-dark fw-bold" variant="link" @click="$router.back()">
                <i class="bi bi-arrow-left"></i>
                <p class="ps-4 m-0 d-inline-block">{{ $t('backTo') }} {{ $t('results') }}</p>
            </b-button>
        </div>
        <b-row>
            <b-col class="product-image-mobile" cols="4">
                <b-card class="border mb-5"
                        :img-src="srcImage"
                        :img-alt="altImage"
                        no-body>
                </b-card>
                <b-row v-if="product.picture_models && product.picture_models.length > 1" :class="['px-3 thumbs', `row-cols-${product.picture_models.length}`]">
                    <b-col :class="['d-flex align-items-center justify-content-center', {'selected': picture.image_url === srcImage }] " v-for="(picture, index) in product.picture_models">
                        <b-img class="cursor-pointer" @click="setSrcImage(index)" :src="picture.thumb_image_url" :alt="picture.alternate_text" />
                    </b-col>
                </b-row>
            </b-col>
            <b-col>
                <b-card class="border-0">
                    <b-card-text>
                        <h3 v-html="product.name"></h3>
                        <hr />
                        <p class="h5 mb-4" v-show="product && product.sku" v-html="`${$t('part')} #${product.sku}`"></p>
                        <p class="h6 mb-4" v-html="product.short_description"></p>

                        <template v-if="mainCategoryId === powerDistributionCategoryId">
                            <p v-if="busRating" class="h6 mb-4" v-html="`${$t('busRating')}: ${busRating}`"></p>
                            <p v-if="positionsPerBus" class="h6 mb-4" v-html="`${$t('positionsPerBus')}: ${positionsPerBus}`"></p>
                            <p v-if="maxPerPosition" class="h6 mb-4" v-html="`${$t('maxPerPosition')}: ${maxPerPosition}`"></p>
                        </template>

                        <b-form-spin-button class="customer-count" min="1" max="100" step="1" v-model="count" aria-controls="panel-1" />
                    </b-card-text>
                </b-card>
            </b-col>
            <b-col class="product-action-mobile mt-3" cols="3" v-if="product.id">
                <product-action :enableDatasheetGeneration="enableDatasheetGeneration"
                                :showDatasheet="product.show_datasheet"
                                :productId="product.id"
                                :quantity="count"
                                :datasheet="datasheetUrl"
                                :installation="installationUrl">
                </product-action>
            </b-col>
        </b-row>

        <template v-if="isMobile">
            <b-accordion free>
                <b-accordion-item class="main-tab" :title="$t('general')">
                    <b-accordion>
                        <b-accordion-item class="sub-tab" :title="$t('productDescription')">
                            <div v-html="product.full_description"></div>
                        </b-accordion-item>
                        <b-accordion-item class="sub-tab" v-if="videos && videos.length" :title="$t('videos')">
                            <div v-for="(video, index) in videos"
                                 :key="index">
                                <iframe v-if="video.published"
                                        class="product-video"
                                        :src="`https://www.youtube.com/embed/${video.embeded_code}`"
                                        frameborder="0"
                                        allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                                        allowfullscreen></iframe>
                            </div>
                        </b-accordion-item>
                    </b-accordion>
                </b-accordion-item>
                <b-accordion-item class="main-tab" :title="$t('specification')">
                    <b-accordion free>
                        <b-accordion-item v-for="(info, index) in productSpecs"
                                          :key="index"
                                          :title="info.name"
                                          class="sub-tab">
                            <div v-if="info.name === $t('mechanical') || info.name === $t('packaging')">
                                <b-row class="my-4">
                                    <b-col cols="12" class="d-flex justify-content-end">
                                        <b-form-checkbox v-model="isMetric" @input="changeMetric()" switch>
                                            {{ $t('convertToMetrics') }}
                                        </b-form-checkbox>
                                    </b-col>
                                </b-row>
                            </div>
                            <b-row v-for="(attribute, indexAttr) in info.attributes"
                                   :key="indexAttr"
                                   class="mobile-spec">
                                <template v-if="attribute.show_on_product_page">
                                    <b-col v-html="attribute.name"></b-col>
                                    <b-col v-html="attribute.values[0].value_raw"></b-col>
                                </template>
                            </b-row>
                        </b-accordion-item>
                    </b-accordion>
                </b-accordion-item>
                <b-accordion-item class="main-tab" v-if="documents && documents.length" :title="$t('downloads')">
                    <b-list-group>
                        <b-list-group-item v-if="enableDatasheetGeneration && product.show_datasheet" button @click="getProductDataSheet()">
                            <div>
                                <i class="bi bi-filetype-pdf"></i>
                                <p class="ps-4 d-inline-block">{{ $t('datasheet') }}_{{ product.sku }}.pdf</p>
                            </div>
                        </b-list-group-item>
                        <b-list-group-item v-for="(document, index) in documents"
                                           :key="index"
                                           :href="document.location_url"
                                           target="_blank">
                            <div>
                                <i class="bi bi-filetype-pdf"></i>
                                <p class="ps-4 d-inline-block">{{ document.title }}</p>
                            </div>
                        </b-list-group-item>
                    </b-list-group>
                </b-accordion-item>
                <b-accordion-item class="main-tab"
                                  v-if="mainCategoryId === powerDistributionCategoryId && crossSellProducts.length"
                                  :title="$t('accessories')">
                    <product-brief class="mt-3" :products="crossSellProducts" @updateProduct="initProduct" />
                </b-accordion-item>
            </b-accordion>
        </template>
        <template v-else>
            <b-tabs class="mb-5 mt-1">
                <b-tab :title="$t('general')">
                    <b-row class="border-bottom mt-3">
                        <b-col cols="2">
                            <p class="product-info text-primary fw-bold">{{ $t('productDescription') }}</p>
                        </b-col>
                        <b-col>
                            <div v-html="product.full_description"></div>
                        </b-col>
                    </b-row>
                    <b-row v-if="videos && videos.length" class="border-bottom">
                        <b-col cols="2">
                            <p class="product-info text-primary fw-bold">{{ $t('videos') }}</p>
                        </b-col>
                        <b-col>
                            <div v-for="(video, index) in videos"
                                 :key="index">
                                <iframe v-if="video.published"
                                        class="product-video"
                                        :src="`https://www.youtube.com/embed/${video.embeded_code}`"
                                        frameborder="0"
                                        allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                                        allowfullscreen></iframe>
                            </div>
                        </b-col>
                    </b-row>
                </b-tab>
                <b-tab v-if="mainCategoryId === powerDistributionCategoryId" :title="$t('specification')">
                    <div>
                        <b-row class="my-4">
                            <b-col cols="12" class="d-flex justify-content-end">
                                <b-form-checkbox v-model="isMetric" @change="changeMetric()" switch>
                                    {{ $t('convertToMetrics') }}
                                </b-form-checkbox>
                            </b-col>
                        </b-row>
                    </div>
                    <div :class="{'product-spec': true, 'product-spec-hidden': hideSpec} ">
                        <b-row v-for="(info, index) in productSpecs"
                               :key="index"
                               class="border-bottom">
                            <b-col cols="2">
                                <p class="text-primary fw-bold">{{ info.name }}</p>
                            </b-col>
                            <b-col>
                                <b-row v-for="(attribute, index) in info.attributes" :key="index">
                                    <template v-if="attribute.show_on_product_page">
                                        <b-col v-html="attribute.name"></b-col>
                                        <b-col v-html="attribute.values[0].value_raw"></b-col>
                                    </template>
                                </b-row>
                            </b-col>
                        </b-row>
                    </div>
                    <div @click="hideSpec = !hideSpec" class="w-100 show-spec">
                        <div v-if="hideSpec" class="opacity-text"></div>
                        <i :class="{'bi': true, 'bi-caret-down-fill': hideSpec, 'bi-caret-up-fill': !hideSpec} "></i>
                        <span class="ps-4 d-inline-block">{{ hideSpec ? $t('seeMore') : $t('seeLess') }}</span>
                    </div>
                </b-tab>
                <b-tab value="downloads" :title="$t('downloads')" class="fw-bold product-documents" v-if="documents && documents.length">
                    <b-list-group>
                        <b-list-group-item v-if="enableDatasheetGeneration && product.show_datasheet" button @click="getProductDataSheet()">
                            <div>
                                <i class="bi bi-filetype-pdf"></i>
                                <p class="ps-4 d-inline-block fw-bold">{{ $t('datasheet') }}_{{ product.sku }}.pdf</p>
                            </div>
                        </b-list-group-item>
                        <b-list-group-item v-for="(document, index) in documents"
                                           :key="index"
                                           :href="document.location_url"
                                           target="_blank">
                            <div>
                                <i class="bi bi-filetype-pdf"></i>
                                <p class="ps-4 d-inline-block">{{ document.title }}</p>
                            </div>
                        </b-list-group-item>
                    </b-list-group>
                </b-tab>
                <b-tab v-if="mainCategoryId === powerDistributionCategoryId && crossSellProducts.length" value="accessories" :title="$t('accessories')" class="fw-bold">
                    <product-brief class="mt-3" :products="crossSellProducts" @updateProduct="initProduct" />
                </b-tab>
            </b-tabs>
        </template>

        <b-container v-if="relatedProducts && relatedProducts.length">
            <h3>{{ $t('relatedProducts') }}</h3>
            <hr />
            <product-brief class="mt-3" :products="relatedProducts" @updateProduct="initProduct" />
        </b-container>
    </b-container>
</template>

<script>
    import {
        BUS_RATING,
        MAX_PER_POSITIONS,
        POSITIONS_PER_BUS,
        DC_POWER_DISTRIBUTION_CATEGORY_ID,
        ELECTRICAL_ATTRIBUTES,
        DOCUMENT_TYPE,
        MAIN_CATEGORY,
        CONVERSION_ATTRIBUTES,
        RATIO_CONVERT_INC_CM,
        LOCALSTORAGE_METRIC_KEY
    } from '@/constants/products-info';
    import { productService } from '@/services';
    import productAction from './product-action.vue';
    import productBrief from '@/components/views/category/product-brief.vue';
    import { MediaPointConstant } from '@/constants/mediaPoint-constants';
    import downloadFileMixin from '@/mixins/download-file-mixin';
    import seoMixin from '@/mixins/seo-mixin';

    export default {
        components: {
            'product-action': productAction,
            'product-brief': productBrief,
        },

        mixins: [downloadFileMixin, seoMixin],

        data: () => ({
            count: 1,
            tab: null,
            product: {},
            busRating: '',
            positionsPerBus: '',
            maxPerPosition: '',
            srcImage: '',
            altImage: '',
            isLoading: false,
            productSpec: [],
            relatedProducts: [],
            crossSellProducts: [],
            documents: [],
            powerDistributionCategoryId: DC_POWER_DISTRIBUTION_CATEGORY_ID,
            datasheetUrl: '',
            installationUrl: '',
            hideSpec: true,
            mainCategoryId: null,
            enableDatasheetGeneration: false,
            videos: [],
            isMetric: localStorage.getItem(LOCALSTORAGE_METRIC_KEY) === 'true'
        }),

        async created() {
            await this.initProduct(this.$route.params.productId);
        },

        computed: {
            isMobile() {
                return window.innerWidth <= MediaPointConstant.MediaPointMd;
            },

            productSpecs() {
                return this.productSpec.filter(spec => spec.name)
            }
        },

        methods: {
            async initProduct(productId) {
                this.$root.showOverlay();

                let data = await productService.getProductDetails(productId);

                let partLang = this.$t('part');

                this.setMetaTags(data.product_details_model.name, `${partLang} # ${data.product_details_model.short_description}`);

                this.product = data.product_details_model;
                this.videos = this.product.videos;
                this.enableDatasheetGeneration = data.enable_datasheet_generation;

                this.productSpec = this.product.product_specification_model.groups;

                if (this.productSpec && this.productSpec.length > 1) {
                    let elektricalAttributes = this.productSpec.find(prd => prd.name === this.$t('electrical'));

                    if (elektricalAttributes) {
                        this.busRating = elektricalAttributes.attributes.find(att => att.name === this.$t('busRating'))?.values[0]?.value_raw || '';

                        this.positionsPerBus = elektricalAttributes.attributes.find(att => att.name === this.$t('positionsPerBus'))?.values[0]?.value_raw  || '';

                        this.maxPerPosition = elektricalAttributes.attributes.find(att => att.name === this.$t('maxPerPosition'))?.values[0]?.value_raw || '';
                    }
                }

                this.mainCategoryId = this.product.breadcrumb.category_breadcrumb[MAIN_CATEGORY].id;

                this.srcImage = this.product.default_picture_model.image_url;
                this.altImage = this.product.default_picture_model.alternate_text;

                this.relatedProducts = this.product.related_products;
                this.crossSellProducts = this.product.cross_sell_products;
                this.documents = this.product.documents.filter(document => document.published)

                this.documents.forEach(document => {
                    if (document.type === DOCUMENT_TYPE.DATASHEET)
                        this.datasheetUrl = document.location_url
                    if (document.type === DOCUMENT_TYPE.INSTALLATION)
                        this.installationUrl = document.location_url
                })

                //Update product attributes if is already selected cm metric
                if (this.isMetric)
                    this.changeMetric();

                this.$root.hideOverlay();
            },

            async getProductDataSheet() {
                let response = await productService.getProductDataSheet(this.$route.params.productId, this.isMetric);

                this.fileAutoDownload(response.data, this.getFileNameFromAxiosResponseHeaders(response.headers));
            },

            increase() {
                this.count++;
            },

            decrease() {
                if (this.count !== 1)
                    this.count--;
            },

            changeMetric() {
                let self = this;
                this.productSpecs.forEach(function (info) {
                    info.attributes.forEach(function (attribute) {
                        if (CONVERSION_ATTRIBUTES.includes(attribute.name)) {
                            attribute.values.forEach(function (value) {
                                if (value.value_raw.toLowerCase().endsWith("in.") || value.value_raw.toLowerCase().endsWith("cm"))
                                {
                                    //Remove text to leave only numbers;
                                    value.value_raw = value.value_raw
                                        .toLowerCase()
                                        .replace('in.', '')
                                        //Exists cases without dot
                                        .replace('in', '')
                                        .replace('cm', '');

                                    //Convert values from inc to mm and mm to inc
                                    value.value_raw = self.isMetric ? value.value_raw * RATIO_CONVERT_INC_CM : value.value_raw / RATIO_CONVERT_INC_CM;

                                    //Leave only two digit after the comma
                                    value.value_raw = value.value_raw.toFixed(!self.isMetric && !value.value_raw.toString().includes(".") ? 0 : 2);

                                    //Add text with metric
                                    value.value_raw += self.isMetric ? " cm" : " In."
                                }
                            });
                        }
                    });
                });

                this.isInit = false;

                localStorage.setItem(LOCALSTORAGE_METRIC_KEY, this.isMetric);
            },

            setSrcImage(index) {
                this.srcImage = this.product.picture_models[index].image_url;
                this.altImage = this.product.picture_models[index].alternate_text;
            }
        },

        watch: {
            async "$route.path"() {
                await this.initProduct(this.$route.params.productId);
            }
        },
    };
</script>